import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/setup-status").then((r) => {
      if (r.data.needs_setup) navigate("/setup", { replace: true });
    }).catch(() => {});
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.08),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(239,68,68,0.05),transparent_40%)]" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20">
            <ShieldCheck className="w-8 h-8 text-black" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 tracking-tight">Auto Detail Manager</h1>
          <p className="text-sm text-slate-500 mt-1">Espace de gestion — connexion sécurisée</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-5"
          data-testid="login-form"
        >
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700">Adresse email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
              required
              data-testid="input-email"
              className="bg-slate-50 border-slate-200 h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="input-password"
              className="bg-slate-50 border-slate-200 h-11"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="btn-login"
            className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Se connecter
          </Button>

          <a href="/forgot-password" data-testid="link-forgot" className="block text-center text-xs text-slate-500 hover:text-emerald-600">Mot de passe oublié ?</a>
        </form>

        <p className="text-center text-sm text-slate-600 mt-6">
          Nouveau professionnel ?{" "}
          <a href="/creer-mon-compte" className="text-emerald-600 font-medium hover:underline" data-testid="link-register">
            Créer mon compte
          </a>
        </p>

        <p className="text-center text-xs text-slate-500 mt-6">
          Client ? Utilisez le lien de réservation personnel que votre professionnel vous a communiqué pour prendre rendez-vous.
        </p>
      </div>
    </div>
  );
}
